import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CityId, Place } from '../types';
import { CITIES, isCityId } from '../lib/cities';
import { attributions, loadCity, prefetchCity, refreshCertification, type LiveCheck } from '../lib/data';
import { trustRank } from '../lib/trust';
import { haversine, useGeolocation } from '../lib/geo';
import { useSaved } from '../lib/useSaved';
import { MapCanvas } from '../components/MapCanvas';
import { BottomSheet, type Detent } from '../components/BottomSheet';
import { Filters, EMPTY_FILTERS, type FilterState } from '../components/Filters';
import { PlaceRow } from '../components/PlaceRow';
import { PlaceDetail } from '../components/PlaceDetail';
import { TopBar } from '../components/TopBar';
import { TrustBanner } from '../components/TrustBanner';
import './Discover.css';
import { useT } from '../i18n';

const NEARBY_RADIUS_M = 2000;

export function Discover() {
    const { t, tn } = useT();
    const { cityId, placeId } = useParams();
    const navigate = useNavigate();
    const city: CityId = isCityId(cityId) ? cityId : 'hongkong';

    const [places, setPlaces] = useState<Place[]>([]);
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
    const [live, setLive] = useState<LiveCheck | null>(null);
    const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
    const [detent, setDetent] = useState<Detent>('half');
    const { toggle, isSaved } = useSaved();
    const position = useGeolocation();

    // Keep typing responsive on a 550-item list.
    const query = useDeferredValue(filters.query);

    // --- load, then re-check certification against the issuing body ---------
    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        setLive(null);
        setFilters((f) => ({ ...EMPTY_FILTERS, query: f.query }));

        loadCity(city)
            .then((loaded) => {
                if (cancelled) return;
                setPlaces(loaded);
                setStatus('ready');

                // Not awaited: the map paints from the snapshot immediately and
                // this patches trust fields in when it lands.
                void refreshCertification(city, loaded).then((result) => {
                    if (cancelled || !result) return;
                    setLive(result);
                    setPlaces([...loaded]);
                });
            })
            .catch(() => { if (!cancelled) setStatus('error'); });

        return () => { cancelled = true; };
    }, [city]);

    // Warm the next most likely city while the user reads this one.
    useEffect(() => {
        const timer = setTimeout(() => {
            for (const other of Object.keys(CITIES) as CityId[]) if (other !== city) prefetchCity(other);
        }, 2500);
        return () => clearTimeout(timer);
    }, [city]);

    // --- distance ------------------------------------------------------------
    const withDistance = useMemo(() => {
        if (!position) return places;
        return places.map((p) => ({ ...p, distance: haversine(position.lat, position.lng, p.lat, p.lng) }));
    }, [places, position]);

    // --- filtering ------------------------------------------------------------
    const counts = useMemo(() => ({
        certified: withDistance.filter((p) => p.halal && ['certified', 'certified_kitchen'].includes(p.halal.status)).length,
        expired: withDistance.filter((p) => p.certState === 'expired' || p.certState === 'delisted').length,
        nearby: position ? withDistance.filter((p) => (p.distance ?? Infinity) <= NEARBY_RADIUS_M).length : 0,
    }), [withDistance, position]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        let out = withDistance;

        if (q) {
            out = out.filter((p) =>
                p.name.toLowerCase().includes(q)
                || p.nameLocal?.toLowerCase().includes(q)
                || p.district?.toLowerCase().includes(q)
                || p.address?.toLowerCase().includes(q)
                || p.cuisines?.some((c) => c.toLowerCase().includes(q)));
        }
        if (filters.types.size) out = out.filter((p) => filters.types.has(p.type));
        if (filters.certifiedOnly) out = out.filter((p) => p.halal && ['certified', 'certified_kitchen'].includes(p.halal.status));
        if (filters.hideExpired) out = out.filter((p) => p.certState !== 'expired' && p.certState !== 'delisted');
        if (filters.nearMe) out = out.filter((p) => (p.distance ?? Infinity) <= NEARBY_RADIUS_M);

        // Nearest first when we know where the user is; otherwise strongest,
        // most current claim first.
        return [...out].sort((a, b) => (
            position && a.distance != null && b.distance != null
                ? a.distance - b.distance
                : trustRank(a) - trustRank(b) || a.name.localeCompare(b.name)
        ));
    }, [withDistance, query, filters, position]);

    // Fall back to the unfiltered set so a shared link still opens a place even
    // when the current filters would have hidden it.
    const selected: Place | undefined = placeId
        ? results.find((p) => p.id === placeId) ?? withDistance.find((p) => p.id === placeId)
        : undefined;
    const selectedId: string | undefined = selected?.id;

    const select = useCallback((p: Place) => {
        navigate(`/city/${city}/place/${encodeURIComponent(p.id)}`);
        setDetent((d) => (d === 'peek' ? 'half' : d));
    }, [city, navigate]);

    const back = useCallback(() => navigate(`/city/${city}`), [city, navigate]);

    const sheetInset = detent === 'full' ? 0 : Math.round(window.innerHeight * (detent === 'half' ? 0.52 : 0.18));

    return (
        <div className="discover">
            {/* The screen is a map, so there is nowhere sensible to put a visible
                page title — but a document with no h1 leaves screen-reader users
                with no outline at all, which is what v2 shipped. */}
            <h1 className="visually-hidden">
                {t('results.heading', { city: CITIES[city].name })}
                {selected ? ` — ${selected.name}` : ''}
            </h1>

            <MapCanvas
                places={results}
                center={CITIES[city].center}
                selectedId={selectedId}
                userPosition={position ?? undefined}
                onSelect={select}
                bottomInset={sheetInset}
            />

            <TopBar
                city={city}
                query={filters.query}
                onQuery={(query) => setFilters((f) => ({ ...f, query }))}
                onCity={(next) => navigate(`/city/${next}`)}
                resultCount={results.length}
            />

            <BottomSheet detent={detent} onDetentChange={setDetent} label={selected ? selected.name : t('results.heading', { city: CITIES[city].name })}>
                {selected ? (
                    <PlaceDetail place={selected} saved={isSaved(selected.id)} onToggleSave={toggle} onBack={back} />
                ) : (
                    <>
                        <Filters value={filters} onChange={setFilters} counts={counts} hasLocation={!!position} />
                        <TrustBanner city={city} places={withDistance} live={live} />

                        {status === 'loading' && <p className="discover__msg">{t('results.loading', { city: CITIES[city].name })}</p>}
                        {status === 'error' && (
                            <p className="discover__msg discover__msg--error">
                                {t('results.error', { city: CITIES[city].name })}
                            </p>
                        )}

                        {status === 'ready' && results.length === 0 && (
                            <div className="discover__empty">
                                <p><strong>{t('results.emptyTitle')}</strong></p>
                                <p>{t('results.emptyBody')}</p>
                                <button type="button" className="btn" onClick={() => setFilters(EMPTY_FILTERS)}>{t('results.clearAll')}</button>
                            </div>
                        )}

                        {results.length > 0 && (
                            <>
                                <p className="discover__count" aria-live="polite">
                                    {tn('results.count_one', 'results.count', results.length)}
                                    {position ? t('results.nearestFirst') : ''}
                                </p>
                                <ul className="discover__list">
                                    {results.map((p) => (
                                        <PlaceRow key={p.id} place={p} selected={p.id === selectedId} onSelect={select} />
                                    ))}
                                </ul>
                            </>
                        )}

                        <footer className="discover__attrib">
                            {(attributions.get(city) ?? []).map((a) => <p key={a}>{a}</p>)}
                        </footer>
                    </>
                )}
            </BottomSheet>
        </div>
    );
}
